import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { COLOR_MODES } from 'lib/user-preferences';
import { focus } from 'entities/block/actions';
import { selectSelected } from 'entities/selected/reducer';
import { selectStepChild } from 'entities/step-children/reducer';
import { ContentShape, StyleShape } from 'entities/step-children/shape';
import { IMAGE_WITH_TEXT, getParentBlock } from 'lib/block';
import transformStyles from 'components/Editor/Primitives/transform-styles';
import interactionStyles from 'components/Editor/Primitives/interaction-styles';

const Frame = styled.div`
  display: flex;
  justify-content: center;
  ${({ customStyle }) => customStyle}

  ${({ isImageWithText }) => !isImageWithText && 'overflow: hidden;'}

  // SDK doesn't support horizontal alignment when fill mode is set
  // so we force to be always at center
  ${({ contentMode, isCroppedImage }) =>
    contentMode === 'fill' && isCroppedImage && 'justify-content: center;'}
`;

const Picture = styled.img`
  width: 100%; // Fit to container
  align-self: center;

  ${({ isParentSelected, isSelected }) =>
    interactionStyles({
      unreach: !isParentSelected,
      selected: isSelected,
      hovered: !isSelected,
    })}

  ${({ contentMode }) => {
    switch (contentMode) {
      case 'fit':
        return 'object-fit: contain';
      case 'fill':
        return `
          object-fit: cover;
          height: 100%;
        `;
      default:
        return '';
    }
  }}
`;

function Image({
  accessibilityLabel,
  contentMode,
  imageUrl,
  id,
  content,
  style,
  colorMode,
  selectedBlock,
  onClick,
}) {
  const $frame = useRef();
  const $picture = useRef();
  const [isCroppedImage, setIsCroppedImage] = useState(false);

  // We check if the image is bigger than the frame
  // if it's bigger it means it's a cropped image
  useEffect(() => {
    if ($picture.current?.clientWidth > $frame.current?.clientWidth) {
      setIsCroppedImage(true);
    } else {
      setIsCroppedImage(false);
    }
  }, [style]);

  const { id: parentId, blockType } = getParentBlock(content, id) ?? {};
  const isParentSelected = selectedBlock === parentId;
  const isSelected = selectedBlock === id;

  // If it's a single image, it's going to be selected
  // by the BlockContainer component, so we don't need to handle the click here
  const handleOnClick = () => {
    if (selectedBlock !== id && blockType === IMAGE_WITH_TEXT) {
      onClick(id);
    }
  };

  // The margin on iOS shrinks the image, behaving more like padding on Web
  // so we need to remap the margin to padding so it looks similar on preview.
  // We break those styles and pass to the frame, so the border stays on image
  // but the padding happens outside
  const {
    justifyContent,
    marginTop: paddingTop,
    marginBottom: paddingBottom,
    marginInlineStart: paddingLeft,
    marginInlineEnd: paddingRight,
    ...styles
  } = transformStyles(style, colorMode);

  const pictureStyles = {
    ...(blockType === IMAGE_WITH_TEXT && {
      alignSelf: 'flex-start',
      maxWidth: 'inherit',
    }),
    ...styles,
  };

  return (
    <Frame
      ref={$frame}
      onClick={handleOnClick}
      customStyle={{
        justifyContent,
        paddingTop,
        paddingBottom,
        paddingLeft,
        paddingRight,
      }}
      isImageWithText={blockType === IMAGE_WITH_TEXT}
      contentMode={contentMode}
      isCroppedImage={isCroppedImage}
    >
      <Picture
        ref={$picture}
        id={id}
        src={imageUrl}
        contentMode={contentMode}
        alt={accessibilityLabel}
        style={pictureStyles}
        isSelected={isSelected}
        isParentSelected={isParentSelected}
        data-testid={id}
      />
    </Frame>
  );
}

Image.propTypes = {
  id: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  contentMode: PropTypes.oneOf(['fill', 'fit']),
  intrinsicSize: PropTypes.shape({
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
  }),
  accessibilityLabel: PropTypes.string,
  style: StyleShape,
  colorMode: PropTypes.oneOf(COLOR_MODES),
  selectedBlock: PropTypes.string,
  content: ContentShape,
  onClick: PropTypes.func,
};

const mapStateToProps = state => {
  const { stepChild: stepId, block: selectedBlock } =
    selectSelected(state) ?? {};
  const { content } = selectStepChild(state, stepId) ?? {};
  return { content, selectedBlock };
};

const mapDispatchToProps = {
  onClick: focus,
};

export default connect(mapStateToProps, mapDispatchToProps)(Image);
